/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { AiResponse, AiType, ChatBotWrite, GenerativeWrite } from '@squiz/dxp-ai-client';
import { AiSchema } from '@squiz/dxp-ai-types';
import { NotEmptyValidator, TextLengthConstraints, TextLengthValidator } from '@squiz/dxp-console-support';
import {
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_STRONG,
  BUTTON_THEME_DEFAULT,
  BUTTON_THEME_SUCCESS,
  Button,
  CHIP_THEME_DEFAULT,
  CHIP_TYPE_REMOVABLE,
  Divider,
  FORM_THEME_LITE,
  Form,
  MultiChipField,
  MultiChipFieldProps,
  TEXTFIELD_TYPE_TEXTAREA,
  TextField,
  TextFieldProps,
  Toggle,
  ToggleProps,
} from '@squiz/sds';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { from } from 'rxjs/internal/observable/from';
import { AI_STRINGS } from '../../../Constants/Strings.constants';
import { getAiService } from '../../../Services/ai';
import { isGenerative } from '../../../Utilities/formPredicates';
import { nameLengthConstraints } from '../../CreateToolDialog/CreateToolDialogForm/CreateToolDialogForm';
import { EDIT_TOOL_DATA_IDS, EDIT_TOOL_FORM_IDS } from '../EditToolDialog.constants';
import { EditToolDialogFormProps } from './EditToolDialogForm.props';

export const instructionsLengthConstraints: TextLengthConstraints = {
  max: 5000,
  min: 3,
};

type FormLabels = {
  apiError: string;
  cancel: string;
  description: string;
  editTool: string;
  enableToggleFieldDescription: string;
  enableTool: string;
  instructionFieldDescription: string;
  instructions: string;
  instructionsLengthMessage: string;
  name: string;
  nameFieldDescription: string;
  nameLengthMessage: string;
  required: string;
  save: string;
  tags: string;
  tagsFieldDescription: string;
};

/* TODO: either Duplicate to EditChatbotDialogForm or possibly make generic for either updating content tool (type : 'generative)
or updating chatbot (type: 'chatbot'), (whatever is easier for testing i think)
- labels
- calling the right update function on the api client
/*
/**
 * The dialog for editing a content tool.
 * @param {DialogContentProps} props Props passed to `<EditToolDialogForm />`.
 * @returns {JSX.Element} The `<EditToolDialogForm />` component.
 */
export const EditToolDialogForm = (props: EditToolDialogFormProps): JSX.Element => {
  const { contentTool } = props.customProps;
  const { t } = useTranslation();
  const { setButtonKeys, setButtons, close } = props;
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
  const aiService = getAiService();
  const contentToolNameRef = useRef<HTMLInputElement>(null);
  const instructionsRef = useRef<HTMLInputElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const tagsRef = useRef<HTMLInputElement>(null);
  const [toolEnabled, setToolEnabled] = useState(contentTool.enabled);
  const [tags, setTags] = useState(contentTool.tags);
  const [isTagsValid, setTagsValid] = useState(true);

  const labelsForType = useCallback(
    (type: AiType): FormLabels => {
      const formLabelsMap: Record<AiType, FormLabels> = {
        chatbot: {
          apiError: t(AI_STRINGS.apiError),
          cancel: t(AI_STRINGS.cancel),
          description: t(AI_STRINGS.availableToolsDescription),
          editTool: t(AI_STRINGS.edit),
          enableToggleFieldDescription: t(AI_STRINGS.enableToggleFieldDescriptionChatbot),
          enableTool: t(AI_STRINGS.enableChatbot),
          instructionFieldDescription: t(AI_STRINGS.instructionFieldDescriptionChatbot),
          instructions: t(AI_STRINGS.instructions),
          instructionsLengthMessage: t(AI_STRINGS.instructionsFieldLength, {
            max: instructionsLengthConstraints.max,
            min: instructionsLengthConstraints.min,
          }),
          name: t(AI_STRINGS.name),
          nameFieldDescription: t(AI_STRINGS.nameFieldDescriptionChatbot),
          nameLengthMessage: t(AI_STRINGS.nameFieldLength),
          required: t(AI_STRINGS.requiredField),
          save: t(AI_STRINGS.save),
          tags: t(AI_STRINGS.tags),
          tagsFieldDescription: t(AI_STRINGS.tagsFieldDescription),
        },
        generative: {
          apiError: t(AI_STRINGS.apiError),
          cancel: t(AI_STRINGS.cancel),
          description: t(AI_STRINGS.availableToolsDescription),
          editTool: t(AI_STRINGS.edit),
          enableToggleFieldDescription: t(AI_STRINGS.enableToggleFieldDescriptionGenerative),
          enableTool: t(AI_STRINGS.enableTool),
          instructionFieldDescription: t(AI_STRINGS.instructionFieldDescription),
          instructions: t(AI_STRINGS.instructions),
          instructionsLengthMessage: t(AI_STRINGS.instructionsFieldLength),
          name: t(AI_STRINGS.name),
          nameFieldDescription: t(AI_STRINGS.nameFieldDescriptionGenerative),
          nameLengthMessage: t(AI_STRINGS.nameFieldLength),
          required: t(AI_STRINGS.requiredField),
          save: t(AI_STRINGS.save),
          tags: t(AI_STRINGS.tags),
          tagsFieldDescription: t(AI_STRINGS.tagsFieldDescription),
        },
      };
      return formLabelsMap[type];
    },
    [t],
  );

  const labels = useMemo(() => labelsForType(contentTool.type), [contentTool.type, labelsForType]);

  const notEmptyValidator = useMemo(() => {
    return new NotEmptyValidator<boolean>((): boolean => false);
  }, []);

  const nameLengthValidator = useMemo(() => {
    return new TextLengthValidator<boolean>(
      () => false,
      () => true,
      nameLengthConstraints,
    );
  }, []);

  const instructionsLengthValidator = useMemo(() => {
    return new TextLengthValidator<boolean>(
      () => false,
      () => true,
      instructionsLengthConstraints,
    );
  }, []);

  const [successDone, setSuccessDone] = useState<boolean>(false);
  const [formError, setFormError] = useState<string>(``);
  const [isFormSubmitted, setSubmit] = useState<boolean>(false);

  const validateFields = useCallback(
    (inputs: Array<HTMLInputElement>): void => {
      const nextErrors = { ...fieldErrors };
      inputs.forEach((input: HTMLInputElement): void => {
        const inputValue = input.value;
        const inputFieldId = input.getAttribute(`id`) as string;

        if (
          [EDIT_TOOL_FORM_IDS.contentToolName, EDIT_TOOL_FORM_IDS.contentToolInstructions].includes(inputFieldId) &&
          !notEmptyValidator.validate(String(inputValue))
        ) {
          nextErrors[inputFieldId] = labels.required;
        } else if (
          [EDIT_TOOL_FORM_IDS.contentToolName].includes(inputFieldId) &&
          !nameLengthValidator.validate(String(inputValue))
        ) {
          nextErrors[inputFieldId] = labels.nameLengthMessage;
        } else if (
          [EDIT_TOOL_FORM_IDS.contentToolInstructions].includes(inputFieldId) &&
          !instructionsLengthValidator.validate(String(inputValue))
        ) {
          nextErrors[inputFieldId] = labels.instructionsLengthMessage;
        } else {
          delete nextErrors[inputFieldId];
        }
      });

      setFieldErrors(nextErrors);
    },
    [fieldErrors, instructionsLengthValidator, labels, nameLengthValidator, notEmptyValidator],
  );

  const errorChecker = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      validateFields([event.target]);
    },
    [validateFields],
  );

  const submitHandler = useCallback(() => {
    setSubmit(true);
    const inputs = {
      contentToolName: contentToolNameRef.current as HTMLInputElement,
      instructions: instructionsRef.current as HTMLInputElement,
      toolEnabled: toggleRef.current as HTMLInputElement,
    };

    if (
      !notEmptyValidator.validate(String(inputs.contentToolName.value)) ||
      !notEmptyValidator.validate(String(inputs.instructions.value)) ||
      !nameLengthValidator.validate(String(inputs.contentToolName.value)) ||
      !instructionsLengthValidator.validate(String(inputs.instructions.value)) ||
      !isTagsValid
    ) {
      const inputsToCheck = [inputs.contentToolName, inputs.instructions];
      validateFields(inputsToCheck);
      setSubmit(false);
      return;
    }

    setFieldErrors({});

    if (isGenerative(contentTool.type)) {
      const newGenerativeAi: GenerativeWrite = {
        authType: `dxp`,
        enabled: toolEnabled,
        instruction: inputs.instructions.value,
        name: inputs.contentToolName.value,
        responseTemplate: contentTool.responseTemplate,
        tags,
        type: 'generative',
      };

      lastValueFrom(from(aiService.apiClient.generative.update(contentTool.id, newGenerativeAi)))
        .then((_: AiResponse) => {
          props.customProps.clearCache(new Date().toISOString());
          setSuccessDone(true);
          close();
          return true;
        })
        .catch((err) => {
          setFormError(labels.apiError);
          setSubmit(false);
        });
    } else {
      const newChatBotAi: ChatBotWrite = {
        authType: 'none',
        enabled: toolEnabled,
        instruction: inputs.instructions.value,
        name: inputs.contentToolName.value,
        responseTemplate: contentTool.responseTemplate,
        tags,
        type: 'chatbot',
      };

      lastValueFrom(from(aiService.apiClient.chatbot.update(contentTool.id, newChatBotAi)))
        .then((response: AiResponse) => {
          props.customProps.clearCache(new Date().toISOString());
          setSuccessDone(true);
          close();
          return true;
        })
        .catch((err) => {
          setFormError(labels.apiError);
          setSubmit(false);
        });
    }
    // There seems to be an issue with the dependencies in this array causing lots of re-render.
    // [aiService.apiClient.generative, close, labels.apiError, , notEmptyValidator, validateFields]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTagsValid, tags, toolEnabled]);

  const inputValues = {
    contentToolName: contentTool.name,
  };

  const contentToolNameFieldProps: TextFieldProps = {
    errorMessage: fieldErrors[EDIT_TOOL_FORM_IDS.contentToolName] || null,
    fieldLabel: labels.name,
    id: EDIT_TOOL_FORM_IDS.contentToolName,
    name: EDIT_TOOL_FORM_IDS.contentToolName,
    onChange: (event: ChangeEvent<HTMLInputElement>): void => {
      errorChecker(event);
    },
    required: true,
    value: inputValues.contentToolName,
  };

  const toggleProps = {
    callback: (checked: boolean) => setToolEnabled(checked),
    checked: toolEnabled,
    fieldLabel: labels.enableTool,
  } as ToggleProps;

  const instructionsFieldProps: TextFieldProps = {
    errorMessage: fieldErrors[EDIT_TOOL_FORM_IDS.contentToolInstructions] || null,
    fieldLabel: labels.instructions,
    id: EDIT_TOOL_FORM_IDS.contentToolInstructions,
    name: EDIT_TOOL_FORM_IDS.contentToolInstructions,
    onChange: (event: ChangeEvent<HTMLInputElement>): void => {
      errorChecker(event);
    },
    required: true,
    rows: 5,
    type: TEXTFIELD_TYPE_TEXTAREA,
    value: contentTool.instruction,
  };

  const tagsFieldProps: MultiChipFieldProps = {
    callback: (tag: string) => {
      const result = AiSchema.pick({ tags: true }).safeParse({ tags: [tag] });
      return result.success;
    },
    defaultChips: useMemo(() => {
      return contentTool.tags.map((tag) => ({
        key: tag,
        text: tag,
        theme: CHIP_THEME_DEFAULT,
        type: CHIP_TYPE_REMOVABLE,
      }));
    }, [contentTool.tags]),
    errorMessage: fieldErrors[EDIT_TOOL_FORM_IDS.contentToolTags],
    fieldLabel: labels.tags,
    id: EDIT_TOOL_FORM_IDS.contentToolTags,
    name: EDIT_TOOL_FORM_IDS.contentToolTags,
    onItemsChange: ({ items }) => {
      const result = AiSchema.pick({ tags: true }).safeParse({ tags: items });

      setTags(items);
      setTagsValid(result.success);
      setFieldErrors((prevErrors) => {
        const nextErrors = { ...prevErrors };
        delete nextErrors[EDIT_TOOL_FORM_IDS.contentToolTags];

        if (result.error?.issues[0]) {
          nextErrors[EDIT_TOOL_FORM_IDS.contentToolTags] = result.error.issues[0].message;
        }

        return nextErrors;
      });
    },
    transformChipText: (text: string): string => {
      return text.toLowerCase();
    },
  };

  useEffect(
    () => {
      const cancelButton = (
        <Button
          classes="dialog-cancel-btn"
          dataId={EDIT_TOOL_DATA_IDS.cancelButton}
          format={BUTTON_FORMAT_SOFT}
          key={'cancel-btn'}
          onClick={(): void => {
            close();
          }}
          text={labels.cancel}
          theme={BUTTON_THEME_DEFAULT}
        />
      );

      const saveButton = (
        <Button
          classes="dialog-save-btn"
          dataId={EDIT_TOOL_DATA_IDS.saveButton}
          format={BUTTON_FORMAT_STRONG}
          isLoading={isFormSubmitted}
          key={'save-btn'}
          onClick={(): void => setSubmit(true)}
          text={labels.save}
          theme={BUTTON_THEME_SUCCESS}
        />
      );

      if (isFormSubmitted && !successDone) {
        setButtonKeys([`save`]);
        setButtons([saveButton]);
        submitHandler();
      } else {
        setButtonKeys([`cancel`, `save`]);
        setButtons([cancelButton, saveButton]);
      }
    },
    // There seems to be an issue with the dependencies in this array that causes failure
    //  [close, isFormSubmitted, labels.cancel, labels.create, setButtonKeys, setButtons, submitHandler, successDone],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFormSubmitted, successDone],
  );

  return (
    <Form errorMessage={formError} legend={''} theme={FORM_THEME_LITE}>
      <div className="edit-tool-dialog-form">
        <div className="edit-tool-dialog-form__field">
          <TextField
            {...contentToolNameFieldProps}
            data-id={EDIT_TOOL_DATA_IDS.contentToolName}
            ref={contentToolNameRef}
          />
          <span className="edit-tool-dialog-form__name-description">{labels.nameFieldDescription}</span>
        </div>
        <Divider />
        <div className="edit-tool-dialog-form__field">
          <Toggle {...toggleProps} data-id={EDIT_TOOL_DATA_IDS.contentToolEnabled} ref={toggleRef} />
          <span className="edit-tool-dialog-form__enable-description">{labels.enableToggleFieldDescription}</span>
        </div>
        <Divider />
        <div className="edit-tool-dialog-form__field">
          <TextField
            {...instructionsFieldProps}
            dataId={EDIT_TOOL_DATA_IDS.contentToolInstructions}
            ref={instructionsRef}
          />
          <span className="edit-tool-dialog-form__instructions-description">{labels.instructionFieldDescription}</span>
        </div>
        {isGenerative(contentTool.type) && (
          <>
            <Divider />
            <div className="edit-tool-dialog-form__field">
              <MultiChipField {...tagsFieldProps} dataId={EDIT_TOOL_DATA_IDS.contentToolInstructions} ref={tagsRef} />
              <span className="edit-tool-dialog-form__tags-description">{labels.tagsFieldDescription}</span>
            </div>
          </>
        )}
      </div>
    </Form>
  );
};
